/**
 * Adds functionality to filter tags
 */
class FilterTags {

  static instance = false;

  /**
   * Filter tag container
   * @type {HTMLElement}
   * @private
  */
  #element;

  /**
   * Tag list container element
   * @type {HTMLElement}
   * @private
  */
  #tagList;

  /**
   * Clear all filters button elements
   * @type {HTMLElement[]}
   * @private
  */
  #clearButtons;

  /**
   * Tag list scroll left button element
   * @type {HTMLElement}
   * @private
  */
  #scrollLeftButton;

  /**
   * Tag list scroll right button element
   * @type {HTMLElement}
   * @private
  */
  #scrollRightButton;

  /**
   * Array of selected filter objects
   * @type {Array}
   * @private
  */
  #selectedFilters;

  /**
   * Show more button show in mobile.
   * @type {HTMLElement}
   * @private
  */
  #showMoreButton;

  /**
   * Filter tag elements
   * @type {HTMLElements}
   * @private
  */
  #filterTags;

  /**
   * Constructor. Starts everything up if filter tags element are found
   * @param {HTMLElement} element element to bind filter tags functionality
   * @returns {FilterTags} FilterTags singleton instance
   */
  constructor(element) {
    if (FilterTags.instance) {
      // Return singleton reference if it exists already
      return FilterTags.instance;
    }

    // Store singleton reference for future instances.
    FilterTags.instance = this;

    this.#element = element;
    this.#tagList = this.#element.querySelector('.chips__tags-list');
    this.#scrollLeftButton = this.#element.querySelector('.chips__scroll-back');
    this.#scrollRightButton = this.#element.querySelector('.chips__scroll-forward');
    this.#filterTags = this.#tagList.querySelectorAll('.chips__tag');
    this.#clearButtons = document.querySelectorAll('.car-search-clear-filters');
    this.#showMoreButton = this.#element.querySelector('.chips__showmore');

    this.#selectedFilters = [];

    // Show filter tags if tags are found on page load
    if (this.#filterTags.length) {
      this.#initFilterTags();
    }

    this.#bindElements();
  }

  /**
   * Bind elements
   * @private
   */
  #bindElements() {
    this.#tagList.addEventListener('scroll', this.#handleOverflows.bind(this));
    this.#scrollLeftButton.addEventListener('click', this.#handleScroll.bind(this, 'back'));
    this.#scrollRightButton.addEventListener('click', this.#handleScroll.bind(this, 'forward'));

    this.#clearButtons.forEach(clearButton => clearButton.addEventListener('click', this.#clearAllFilterTags.bind(this)));

    this.#tagList.addEventListener('click', (event) => {
      if (event.target.classList.contains('chips__tag') && !event.target.classList.contains('.disabled')) {
        const filterData = {
          name: event.target.dataset.name,
          value: event.target.dataset.value
        };

        this.#removeFilterTag(filterData, true);
        document.dispatchEvent(new CustomEvent('tagListChange', {
          detail: {
            filters: this.#selectedFilters
          }
        }));
      }
    });

    this.#showMoreButton?.addEventListener('click', () => {
      this.#element.classList.add('chips--mobile-wrap-show');
    })

    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);

      resizeTimeout = setTimeout(() => {
        this.#handleOverflows();
      }, 200);
    });
  }

  #showOrHideMoreButton() {
    if( this.#showMoreButton && this.#selectedFilters.length <= 4 ) {
      this.#showMoreButton.classList.add('hidden');
    }
  }

  /**
   * Init filter tags from dom if there are any found on page load
   * @private
   */
  #initFilterTags() {
    this.#selectedFilters = Array.from(this.#filterTags).map((filterTag) => {
      if (!filterTag.dataset.name || !filterTag.dataset.value || !filterTag.innerText) {
        filterTag.remove();
        return false;
      }

      return {
        name: filterTag.dataset.name,
        text: filterTag.innerText,
        value: filterTag.dataset.value
      };
    }).filter(Boolean);

    this.#handleOverflows();
  }

  /**
   * Add filter tag
   * @param {Object} filter Filter object
   * @private
   */
  #addFilterTag(filter) {
    const filterElement = document.createElement('button');
    filterElement.classList.add('chips__tag', 'chips__tag--hidden', 'button-text', 'button-text--tertiary');
    filterElement.dataset.name = filter.name;
    filterElement.dataset.value = filter.value;
    filterElement.textContent = filter.text;

    this.#tagList.appendChild(filterElement);

    setTimeout(() =>{
      filterElement.classList.remove('chips__tag--hidden');

      setTimeout(() => {
        this.#handleOverflows();
      }, 200);
    },0);
  }

  /**
   * Remove filter tag
   * @param {Object} filter Filter object
   * @param {Boolean} runCarSearch Run car search. Default is false.
   * @private
   */
  #removeFilterTag(filter, runCarSearch = false) {
    const tagElement = this.#tagList.querySelector(`[data-name="${filter.name}"][data-value="${filter.value}"]`);
    const tagIndex = this.#selectedFilters.findIndex((item) => item?.name === filter.name && item?.value === filter.value);

    if (tagElement) {
      let removeTimeout = 400;
      if (tagElement.classList.contains('last-tag')) {
        removeTimeout = 200;
      }

      if (tagIndex > -1) {
        this.#selectedFilters.splice(tagIndex, 1);
      }

      tagElement.classList.add('chips__tag--hidden');

      setTimeout(() => {
        tagElement.remove();

        if (!this.#selectedFilters.length) {
          this.#toggleFilterTags('hide');
        }

        this.#handleOverflows();
      }, removeTimeout);
    }
  }

  /**
   * Remove filter
   * @private
   */
  #clearAllFilterTags() {
    this.#selectedFilters.forEach(filter => this.#removeFilterTag(filter));
    this.#toggleFilterTags('hide');

    document.dispatchEvent(new CustomEvent('tagListChange', {
      detail: { filters: [] }
    }));
  }

  /**
   * Toggle visibility of the filter tags
   * @param {String} action Toggle action. Value "hide" hides tags. Default is to make them visible.
   * @private
   */
  #toggleFilterTags(action) {
    if(action === 'hide') {
      this.#element.classList.add('chips--hidden')
    } else {
      this.#element.classList.remove('chips--hidden');
    }
  }

  /**
   * Handle tag list scroll event
   * @private
   */
  #handleOverflows() {
    const scrollLeftPos = this.#tagList.scrollLeft;
    const scrollRightPos = this.#tagList.scrollWidth - ( this.#tagList.scrollLeft + this.#tagList.clientWidth );

    scrollLeftPos > 0 ? this.#element.classList.add('overflow-left') : this.#element.classList.remove('overflow-left');
    scrollRightPos > 0 ? this.#element.classList.add('overflow-right') : this.#element.classList.remove('overflow-right');
  }

  /**
   * Handle tag list scroll actions
   * @param {String} direction Direction of the scroll. Value "back" scrolls backwards. Default is forwards.
   * @private
   */
  #handleScroll(direction) {
    const directionCorrection = direction === 'back' ? -1 : 1;
    const scrollPosition = this.#tagList.scrollLeft;
    const scrollAmount = directionCorrection * (this.#tagList.clientWidth / 2); // Scroll half the width of the container
    let scrollToPosition = scrollPosition + scrollAmount;

    if (scrollToPosition < 50) {
      scrollToPosition = 0; // Just scroll to the beginning if scroll to position is in range of 50px
    } else if (this.#tagList.scrollWidth - scrollToPosition - this.#tagList.clientWidth < 50) {
      scrollToPosition = this.#tagList.scrollWidth; // Just scroll to the end if scroll to position is in range of 50px
    }

    this.#tagList.scrollTo({
      left: scrollToPosition,
      behavior: 'smooth'
    });
  }

  /**
   * Update filter tags
   * @param {Array} filters Array of filter objects
   */
  updateFilterTags(filters = []) {
    if (Array.isArray(filters)) {
      let newFilters = [];
      let removedFilters = [];

      filters.forEach((filter) => {
        if ( !this.#selectedFilters.filter((item) => item?.name === filter.name && item?.value === filter.value).length ) {
          newFilters.push(filter);
        }
      });

      this.#selectedFilters.forEach((filter) => {
        if ( !filters.filter((item) => item?.name === filter.name && item?.value === filter.value).length ) {
          removedFilters.push(filter);
        }
      });

      // Set last-tag class to tags that will be removed and are last elements in the tag list
      const currentTagsReversed = Array.from(this.#tagList.querySelectorAll('.chips__tag')).reverse();
      currentTagsReversed.forEach((filterElement) => {
        const lastTag = currentTagsReversed.filter((tag) => !tag.classList.contains('last-tag'))[0];

        if (!lastTag) {
          return;
        }

        const lastTagMatch = (filterElement.dataset.value === lastTag.dataset.value && filterElement.dataset.name === lastTag.dataset.name);
        const removedFilterMatch = removedFilters.some((removedFilter) => removedFilter.value === filterElement.dataset.value && removedFilter.name === filterElement.dataset.name);

        if (lastTagMatch && removedFilterMatch) {
          filterElement.classList.add('last-tag');
        }
      });

      if (removedFilters.length) {
        removedFilters.forEach((filter) => {
          this.#removeFilterTag(filter);
        });
      }

      if (newFilters.length) {
        newFilters.forEach((filter) => {
          this.#addFilterTag(filter);
        });
      }

      this.#toggleFilterTags(filters.length ? 'show' : 'hide');

      this.#selectedFilters = filters;

      this.#showOrHideMoreButton();
    }
  }

  /**
   * Return amount of filters currently active.
   * @return {number} 
   */
  get amount() {
    return this.#selectedFilters.length;
  }

  /**
   * init Static initializer of the class.
   */
  static init() {
    const filterTags = document.querySelector('.filter-tags');

    if (filterTags) {
      new FilterTags(filterTags);
    }
  }
}

export default FilterTags;
